import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandLockup } from "@/components/Branding";
import { ForgotPasswordForm } from "@/components/diary/ForgotPasswordForm";

const inputCls = "mt-1 w-full rounded-md border border-[#4A4540]/15 bg-white px-4 py-2.5 text-sm text-[#2C2A28] focus:outline-none focus:ring-2 focus:ring-[#E07A5A]";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(token ? "checking" : "invalid");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    api.post("/auth/reset-password/check", { token })
      .then((res) => setStatus(res.data.valid ? "valid" : "invalid"))
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setError("Your new password needs at least 8 characters."); return; }
    if (pw !== pw2) { setError("The two passwords don't match."); return; }
    setError(""); setLoading(true);
    try {
      const res = await api.post("/auth/reset-password", { token, password: pw });
      setDone(res.data.message);
    } catch (err) {
      if (err.response?.status === 400 && /invalid or has expired/.test(err.response?.data?.detail || "")) { setStatus("invalid"); return; }
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset your password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF4E8] px-4 py-8 text-[#2C2A28]">
      <div className="mx-auto max-w-md">
        <BrandLockup />
        <div className="mt-10 rounded-2xl border border-[#4A4540]/10 bg-white p-6 shadow-sm sm:p-8" data-testid="reset-password-card">
          <h1 className="font-serif text-3xl">Choose a new password</h1>
          {done ? (
            <>
              <p data-testid="reset-password-done" className="mt-3 text-sm text-[#4A4540]">{done}</p>
              <Link to="/account" data-testid="reset-back-signin" className="mt-6 inline-block rounded-full bg-[#E07A5A] px-6 py-2.5 text-sm font-semibold text-white">Go to sign in</Link>
            </>
          ) : status === "checking" ? (
            <p data-testid="reset-password-checking" className="mt-3 text-sm text-[#6B635B]">Checking your reset link…</p>
          ) : status === "invalid" ? (
            <div data-testid="reset-password-expired">
              <p className="mt-3 text-sm leading-relaxed text-[#A84A32]">This reset link has expired or has already been used. For your security, each link works once and only for 30 minutes.</p>
              <p className="mt-2 text-sm text-[#4A4540]">Enter your username or email below and we'll send you a fresh link straight away.</p>
              <div className="mt-4 rounded-xl bg-[#2E3B31] p-5">
                <ForgotPasswordForm onBack={() => navigate("/account")} />
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4">
              <label className="text-xs font-medium text-[#4A4540]">New password</label>
              <input data-testid="reset-password-input" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className={inputCls} />
              <label className="mt-3 block text-xs font-medium text-[#4A4540]">Confirm new password</label>
              <input data-testid="reset-password-confirm" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} />
              {error && <p data-testid="reset-password-error" className="mt-2 text-xs text-[#A84A32]">{error}</p>}
              <button data-testid="reset-password-submit" type="submit" disabled={loading} className="mt-5 w-full rounded-full bg-[#E07A5A] py-2.5 text-sm font-semibold text-white hover:bg-[#d66b4b] disabled:opacity-60">{loading ? "Saving…" : "Save new password"}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
